import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  Library,
  Star,
  Sparkles,
  ShieldAlert,
  RefreshCw,
  Loader2,
  Download,
  Trash2,
  RotateCcw,
  AlertTriangle,
  PlayCircle,
  EyeOff,
  Eye,
} from "lucide-react";
import {
  fetchFavorites,
  fetchLibrary,
  fetchInvalidVideos,
  removeFavorite,
  markVideoInvalid,
  restoreVideo,
  deleteLibraryEntry,
  refreshCache,
  exportLibrary,
  fetchBlacklist,
  hideSong,
  unhideSong,
  setContentLabel,
  backfillLabels,
} from "../../lib/api";
import { classifyVersionLabels, CONTENT_LABELS } from "../../lib/labels";
import { LabelChips } from "./LabelChips";
import { toast } from "sonner";

const REFRESH_MODES = [
  { value: "favorites", label: "Favoritos" },
  { value: "learned", label: "Biblioteca aprendida" },
  { value: "popular", label: "Más usadas" },
  { value: "warm-cache", label: "Warm cache" },
  { value: "all", label: "Todo" },
];

const SongRow = ({ song, right }) => (
  <div className="flex items-center gap-3 p-2.5 border-b border-border last:border-0 hover:bg-white/5">
    <img src={song.thumbnail || undefined} alt="" className="w-16 h-10 object-cover rounded-sm bg-muted shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        {song.isFavorite && <Star className="w-3 h-3 text-amber-400 shrink-0" fill="currentColor" />}
        {song.isInvalid && (
          <span className="text-[9px] font-mono uppercase text-destructive bg-destructive/10 border border-destructive/30 rounded px-1">
            inválido
          </span>
        )}
        <p className="text-sm font-medium truncate">{song.title}</p>
      </div>
      <LabelChips labels={classifyVersionLabels(`${song.title} ${song.channelTitle || ""}`)} content={song.content_labels} className="mt-0.5" />
      <p className="text-[11px] text-muted-foreground truncate font-mono mt-0.5">
        {song.channelTitle} · usos: {song.useCount ?? 0} · {song.source || "—"}
      </p>
    </div>
    {right}
  </div>
);

const LibraryDialog = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState("favorites");
  const [favorites, setFavorites] = useState([]);
  const [library, setLibrary] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refresh controls
  const [mode, setMode] = useState("favorites");
  const [limit, setLimit] = useState(20);
  const [force, setForce] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);

  const handleBackfill = async () => {
    if (!window.confirm("¿Aplicar labels técnicos e invalidar Carpool Karaoke en el caché existente? No llama a YouTube.")) return;
    setBackfilling(true);
    setBackfillReport(null);
    try {
      const data = await backfillLabels();
      setBackfillReport(data);
      toast.success("Backfill completado", { description: `${data.analyzed} registros analizados` });
      load();
    } catch (e) {
      toast.error("Error en el backfill");
    } finally {
      setBackfilling(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [favs, lib, inv, hid] = await Promise.all([
        fetchFavorites(),
        fetchLibrary("useCount", 500),
        fetchInvalidVideos(),
        fetchBlacklist(),
      ]);
      setFavorites(favs);
      setLibrary(lib);
      setInvalid(inv);
      setHidden(hid);
    } catch (e) {
      toast.error("No se pudo cargar la biblioteca");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRemoveFav = async (videoId) => {
    await removeFavorite(videoId);
    setFavorites((f) => f.filter((x) => x.videoId !== videoId));
    toast("Favorito eliminado");
  };

  const handleMarkInvalid = async (videoId) => {
    await markVideoInvalid(videoId, "manual");
    toast("Marcado como inválido");
    load();
  };

  const handleRestore = async (videoId) => {
    await restoreVideo(videoId);
    toast.success("Video restaurado");
    load();
  };

  const handleDelete = async (videoId) => {
    await deleteLibraryEntry(videoId);
    toast("Eliminado de la biblioteca");
    load();
  };

  const handleHide = async (song) => {
    await hideSong({ title: song.title, channelTitle: song.channelTitle, query: song.query_original || song.title });
    toast("Canción oculta del catálogo");
    load();
  };

  const handleUnhide = async (songKey) => {
    await unhideSong(songKey);
    toast.success("Canción restaurada");
    load();
  };

  const handleContentLabel = async (videoId, label, has) => {
    await setContentLabel(videoId, label, has ? "remove" : "add");
    toast(has ? `Quitado: ${label}` : `Etiquetado: ${label}`);
    load();
  };

  const handleEstimate = async () => {
    setEstimate(null);
    setReport(null);
    try {
      const data = await refreshCache({ mode, limit: Number(limit), force, dryRun: true });
      setEstimate(data);
    } catch (e) {
      toast.error("No se pudo estimar el refresco");
    }
  };

  const handleRefresh = async () => {
    setRunning(true);
    setReport(null);
    try {
      const data = await refreshCache({ mode, limit: Number(limit), force, dryRun: false });
      setReport(data);
      toast.success(`Refresco completado: ${data.refreshed} actualizadas`, {
        description: `${data.quota_consumed} unidades de cuota usadas`,
      });
      setEstimate(null);
      load();
    } catch (e) {
      toast.error("Error al refrescar el caché");
    } finally {
      setRunning(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const data = await exportLibrary(format);
      let blob;
      let filename;
      if (format === "csv") {
        blob = new Blob([data], { type: "text/csv;charset=utf-8" });
        filename = `djgabo-library-${new Date().toISOString().slice(0, 10)}.csv`;
      } else {
        blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        filename = `djgabo-library-${new Date().toISOString().slice(0, 10)}.json`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="library-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Library className="w-5 h-5 text-secondary" />
            Biblioteca DJ Gabo
          </DialogTitle>
          <DialogDescription>
            Favoritos, biblioteca aprendida, videos inválidos, refresco de caché y exportación.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-5 bg-background shrink-0" data-testid="library-tabs">
            <TabsTrigger value="favorites" data-testid="tab-favorites">
              <Star className="w-3.5 h-3.5 mr-1" /> Favoritos ({favorites.length})
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-library">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Biblioteca ({library.length})
            </TabsTrigger>
            <TabsTrigger value="invalid" data-testid="tab-invalid">
              <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Inválidos ({invalid.length})
            </TabsTrigger>
            <TabsTrigger value="hidden" data-testid="tab-hidden">
              <EyeOff className="w-3.5 h-3.5 mr-1" /> Ocultas ({hidden.length})
            </TabsTrigger>
            <TabsTrigger value="refresh" data-testid="tab-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
            </TabsTrigger>
          </TabsList>

          {loading && (
            <div className="py-10 flex justify-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {/* Favorites */}
          <TabsContent value="favorites" className="flex-1 overflow-y-auto mt-3 border border-border rounded-md" data-testid="favorites-list">
            {!loading && favorites.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">Aún no hay favoritos. Marca canciones con ★ desde el buscador.</p>
            )}
            {favorites.map((s) => (
              <div key={s.videoId}>
                {s.isInvalid && (
                  <div className="flex items-center gap-2 px-2.5 pt-2 text-[11px] text-amber-300">
                    <AlertTriangle className="w-3.5 h-3.5" /> Este favorito podría estar roto. Considera elegir otra versión.
                  </div>
                )}
                <SongRow
                  song={s}
                  right={
                    <button
                      onClick={() => handleRemoveFav(s.videoId)}
                      data-testid={`remove-fav-${s.videoId}`}
                      className="text-muted-foreground hover:text-destructive transition-colors p-1.5"
                      title="Quitar favorito"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  }
                />
              </div>
            ))}
          </TabsContent>

          {/* Library */}
          <TabsContent value="library" className="flex-1 overflow-y-auto mt-3 border border-border rounded-md" data-testid="library-list">
            {!loading && library.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">La biblioteca aprendida está vacía.</p>
            )}
            {library.map((s) => (
              <SongRow
                key={s.videoId}
                song={s}
                right={
                  <div className="flex items-center gap-0.5">
                    {CONTENT_LABELS.map((cl) => {
                      const has = (s.content_labels || []).includes(cl);
                      return (
                        <button
                          key={cl}
                          onClick={() => handleContentLabel(s.videoId, cl, has)}
                          data-testid={`content-label-${cl}-${s.videoId}`}
                          title={`${has ? "Quitar" : "Marcar"}: ${cl}`}
                          className={`text-[9px] font-mono font-bold px-1 py-0.5 rounded border transition-colors ${
                            has ? "text-red-300 bg-red-500/10 border-red-500/40" : "text-muted-foreground/50 border-border hover:text-foreground"
                          }`}
                        >
                          {cl === "EXPLÍCITA" ? "E" : cl === "NO RECOMENDADA" ? "NR" : "R"}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handleHide(s)}
                      data-testid={`hide-lib-${s.videoId}`}
                      className="text-muted-foreground hover:text-red-400 transition-colors p-1.5"
                      title="Ocultar canción del catálogo"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMarkInvalid(s.videoId)}
                      data-testid={`mark-invalid-${s.videoId}`}
                      className="text-muted-foreground hover:text-amber-400 transition-colors p-1.5"
                      title="Marcar como inválido"
                    >
                      <ShieldAlert className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(s.videoId)}
                      data-testid={`delete-lib-${s.videoId}`}
                      className="text-muted-foreground hover:text-destructive transition-colors p-1.5"
                      title="Eliminar de la biblioteca"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                }
              />
            ))}
          </TabsContent>

          {/* Hidden / Blacklist */}
          <TabsContent value="hidden" className="flex-1 overflow-y-auto mt-3 border border-border rounded-md" data-testid="hidden-list">
            {!loading && hidden.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">No hay canciones ocultas.</p>
            )}
            {hidden.map((s) => (
              <SongRow
                key={s.song_key}
                song={{ title: s.display_title, channelTitle: s.artist || "", useCount: s.versions_count, source: "oculta", labels: s.labels, content_labels: s.content_labels, thumbnail: s.best_version?.thumbnail }}
                right={
                  <button
                    onClick={() => handleUnhide(s.song_key)}
                    data-testid={`unhide-${s.song_key}`}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary transition-colors px-2 py-1.5 rounded border border-border"
                    title="Restaurar canción"
                  >
                    <Eye className="w-4 h-4" /> Restaurar
                  </button>
                }
              />
            ))}
          </TabsContent>

          {/* Invalid */}
          <TabsContent value="invalid" className="flex-1 overflow-y-auto mt-3 border border-border rounded-md" data-testid="invalid-list">
            {!loading && invalid.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">No hay videos inválidos. ✓</p>
            )}
            {invalid.map((s) => (
              <SongRow
                key={s.videoId}
                song={s}
                right={
                  <button
                    onClick={() => handleRestore(s.videoId)}
                    data-testid={`restore-${s.videoId}`}
                    className="text-muted-foreground hover:text-secondary transition-colors p-1.5"
                    title="Restaurar"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                }
              />
            ))}
          </TabsContent>

          {/* Refresh + Export */}
          <TabsContent value="refresh" className="flex-1 overflow-y-auto mt-3 space-y-4" data-testid="refresh-panel">
            <div className="rounded-lg border border-border bg-surface-elevated/40 p-4 space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5 text-secondary" /> Refrescar caché desde YouTube
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground font-mono">Modo</label>
                  <select
                    value={mode}
                    onChange={(e) => { setMode(e.target.value); setEstimate(null); }}
                    data-testid="refresh-mode-select"
                    className="w-full bg-white/[0.05] border border-white/15 rounded-md h-10 px-2 text-sm text-white focus:border-secondary focus:outline-none"
                  >
                    {REFRESH_MODES.map((m) => (
                      <option key={m.value} value={m.value} className="bg-surface">{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground font-mono">Tope (canciones)</label>
                  <input
                    type="number"
                    value={limit}
                    min={1}
                    max={500}
                    onChange={(e) => { setLimit(e.target.value); setEstimate(null); }}
                    data-testid="refresh-limit-input"
                    className="w-full bg-white/[0.05] border border-white/15 rounded-md h-10 px-2 text-sm text-white focus:border-secondary focus:outline-none font-mono"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={force} onChange={(e) => { setForce(e.target.checked); setEstimate(null); }} data-testid="refresh-force-checkbox" />
                Forzar (refrescar aunque sean recientes)
              </label>

              <div className="flex gap-2">
                <button
                  onClick={handleEstimate}
                  disabled={running}
                  data-testid="refresh-estimate-button"
                  className="flex-1 h-10 rounded-md border border-border hover:border-secondary hover:text-secondary text-sm font-medium disabled:opacity-50"
                >
                  Estimar cuota
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={running || !estimate}
                  data-testid="refresh-run-button"
                  className="flex-1 h-10 rounded-md bg-secondary text-secondary-foreground font-bold text-xs uppercase tracking-wide hover:bg-secondary/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                  title={!estimate ? "Primero estima la cuota" : "Ejecutar refresco"}
                >
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  {running ? "Refrescando..." : "Ejecutar"}
                </button>
              </div>

              {estimate && (
                <div className="rounded-md border border-secondary/30 bg-secondary/[0.06] p-3 text-[12px] font-mono text-muted-foreground" data-testid="refresh-estimate">
                  <p className="text-secondary font-bold mb-1">Estimación</p>
                  <p>Candidatas: <b className="text-foreground">{estimate.candidates}</b> · A refrescar: <b className="text-foreground">{estimate.to_refresh}</b> · Saltadas (recientes): <b className="text-foreground">{estimate.skipped_recent}</b></p>
                  <p>Cuota estimada: <b className="text-foreground">{estimate.quota_estimated}</b> unidades (presupuesto {estimate.quota_budget})</p>
                  <p className="mt-1 text-amber-300">{estimate.message}</p>
                </div>
              )}

              {report && (
                <div className="rounded-md border border-primary/30 bg-primary/[0.06] p-3 text-[12px] font-mono text-muted-foreground" data-testid="refresh-report">
                  <p className="text-primary font-bold mb-1">Reporte de ejecución</p>
                  <p>Refrescadas: <b className="text-foreground">{report.refreshed}</b> · Fallidas: <b className="text-foreground">{report.failed}</b></p>
                  <p>Cuota usada: <b className="text-foreground">{report.quota_consumed}</b> unidades</p>
                  {report.stopped_reason && <p className="text-amber-300">Detenido: {report.stopped_reason}</p>}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-surface-elevated/40 p-4 space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5 text-secondary" /> Exportar biblioteca
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport("json")}
                  data-testid="export-json-button"
                  className="flex-1 h-10 rounded-md border border-border hover:border-secondary hover:text-secondary text-sm font-medium flex items-center justify-center gap-1.5"
                >
                  <Download className="w-4 h-4" /> JSON
                </button>
                <button
                  onClick={() => handleExport("csv")}
                  data-testid="export-csv-button"
                  className="flex-1 h-10 rounded-md border border-border hover:border-secondary hover:text-secondary text-sm font-medium flex items-center justify-center gap-1.5"
                >
                  <Download className="w-4 h-4" /> CSV
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface-elevated/40 p-4 space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <EyeOff className="w-3.5 h-3.5 text-secondary" /> Aplicar labels al caché existente
              </p>
              <p className="text-[11px] text-muted-foreground">
                Detecta labels técnicos (tono, mujer/hombre, piano, acústico) e invalida videos "Carpool Karaoke" en el caché ya guardado. No llama a YouTube.
              </p>
              <button
                onClick={handleBackfill}
                disabled={backfilling}
                data-testid="backfill-labels-button"
                className="w-full h-10 rounded-md border border-border hover:border-secondary hover:text-secondary text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {backfilling ? "Procesando..." : "Aplicar labels al caché"}
              </button>
              {backfillReport && (
                <div className="rounded-md border border-secondary/30 bg-secondary/[0.06] p-3 text-[12px] font-mono text-muted-foreground" data-testid="backfill-report">
                  <p className="text-secondary font-bold mb-1">Resumen del backfill</p>
                  <p>Analizados: <b className="text-foreground">{backfillReport.analyzed}</b></p>
                  <p>Tono bajo: {backfillReport["TONO BAJO"]} · Tono alto: {backfillReport["TONO ALTO"]} · Mujer: {backfillReport.MUJER} · Hombre: {backfillReport.HOMBRE}</p>
                  <p>Piano: {backfillReport.PIANO} · Acústico: {backfillReport.ACUSTICO}</p>
                  <p className="text-amber-300">Carpool invalidados: {backfillReport.carpool_invalidated} · Errores: {backfillReport.errors}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default LibraryDialog;
